import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as spaAnalytics from 'storefront-analytics/dist/spa-analytics';
import AutoPromoAnalytics from 'storefront-analytics/dist/auto-promo';
import AutoFooterAnalytics from 'storefront-analytics/dist/auto-footer';

import { EventEmitter, ExtraData } from '@src/@types/analytics';
import { AnalyticsUserContext } from '@src/contexts/analytics-user-context';
import WindowService from '@src/services/window.service';
import { completeGtmInitialization } from '@src/vendor/spa-analytics-postinit';
import { PAGE_TYPE } from '@src/helpers/analytics.helper';
import { ServerConfigContext } from '@src/contexts/server-config-context';

/**
 * Handles asynchronous, failure-tolerant initialization of SPA Analytics.
 * Exposes a memoized {@link emitEvent} function that can be used as a base for
 * more purpose-built event emitter functions, such as those exposed by
 * {@link useAnalytics}.
 */
export const useInitAnalytics = (
  analyticsRequested: boolean,
  gtmParams: string,
  gtmContainerId: string,
  analyticsExtraData?: ExtraData,
) => {
  const {
    loaded: analyticsUserContextLoaded,
    error: analyticsUserContextError,
    analyticsUserContextResponse,
  } = useContext(AnalyticsUserContext);

  const { akamaiBotHeader } = useContext(ServerConfigContext);

  const [analyticsInitializing, setAnalyticsInitializing] = useState(false);
  const analyticsDisabled = useRef(false);

  // We track whether analytics has been initialized in two places: one to drive
  // conditional rendering, another not tracked by React to avoid re-renders
  // caused by hooks that reference it. This is a performance enhancement.
  // These values must be kept in sync.
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  const analyticsInitializedRef = useRef(false);
  const setInitialized = (value: boolean) => {
    setAnalyticsInitialized(value);
    analyticsInitializedRef.current = value;
  };

  /**
   * Queue of events that were sent by components while analytics was
   * initializing
   */
  const eventQueue = useRef<EventEmitter[]>([]);

  /**
   * Run through all queued events and fire them, then clear the queue.
   */
  const processPreInitEventQueue = useCallback((): void => {
    if (eventQueue.current.length > 0) {
      eventQueue.current.forEach((emitter) => emitSafely(emitter));
      eventQueue.current = [];
    }
  }, [eventQueue]);

  // Initialize SPA Analytics once prerequisite data is available
  useEffect(() => {
    let mounted = true;
    let fallbackTimer: ReturnType<typeof setTimeout> = undefined;

    // Bail out and pretend we successfully initialized analytics if we're
    // either not going to use it in this environment, or the fetch for its init
    // data failed.
    if (!analyticsRequested || analyticsUserContextError) {
      setInitialized(true);
      analyticsDisabled.current = true;
      return;
    }

    // If we are ready to start initialization or it's already in progress,
    // make sure our success callback is written to the window for SPA
    // Analytics to call.
    if (analyticsUserContextLoaded && !analyticsInitialized) {
      if (!WindowService.window.chewy) WindowService.window.chewy = {};
      if (!WindowService.window.dataLayer) WindowService.window.dataLayer = [];

      // Analytics initialization can involve async operations, but the only way
      // they expose completion is through a callback on the window. Wait for
      // this before considering analytics ready.
      WindowService.window.chewy.analyticsCallback = () => {
        completeGtmInitialization(gtmParams, gtmContainerId);
        if (mounted) {
          setInitialized(true);
          setAnalyticsInitializing(false);
          WindowService.window.chewy.analyticsCallback = undefined;

          AutoPromoAnalytics();
          AutoFooterAnalytics();

          processPreInitEventQueue();
        }
        if (fallbackTimer) clearTimeout(fallbackTimer);
      };

      // If Analytics never invokes the success callback for some reason,
      // signal that it succeeded anyway to unblock rendering of components
      // that block on it. It's more important that the application render than
      // we track analytics for it.
      fallbackTimer = setTimeout(() => {
        console.error(
          'Did not receive analytics initialization success callback. ' +
            'Unblocking rendering anyway, but analytics calls will likely ' +
            'fail.',
        );
        if (mounted) {
          setInitialized(true);
          setAnalyticsInitializing(false);
          WindowService.window.chewy.analyticsCallback = undefined;
          analyticsDisabled.current = true;
        }
        if (fallbackTimer) clearTimeout(fallbackTimer);
      }, 500);
    }

    // Only trigger initialization if it was requested, if we didn't already
    // start or complete initialization, and if required data is ready.
    if (
      analyticsUserContextLoaded &&
      !analyticsInitialized &&
      !analyticsInitializing
    ) {
      setAnalyticsInitializing(true);
      // eslint-disable-next-line import/namespace
      spaAnalytics.init({
        pageData: {
          ecomm_pagetype: 'insurance',
          pageType: PAGE_TYPE,
          pageVariation: '',
          parentList: 'insurance',
          siteId: '0',
          subSiteId: '0',
          ...analyticsExtraData,
        },
        userData: analyticsUserContextResponse.userContextData,
        analyticsFeatureToggles: analyticsUserContextResponse.analyticsToggles,
        isBot: akamaiBotHeader.length > 0,
        botHeader: akamaiBotHeader.length > 0 ? akamaiBotHeader : null,
      });
    }

    return () => {
      mounted = false;
      if (WindowService.window.chewy?.analyticsCallback) {
        WindowService.window.chewy.analyticsCallback = undefined;
      }
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [
    analyticsRequested,
    analyticsExtraData,
    analyticsInitializing,
    analyticsInitialized,
    analyticsUserContextLoaded,
    analyticsUserContextResponse,
    analyticsUserContextError,
    processPreInitEventQueue,
    gtmParams,
    gtmContainerId,
    akamaiBotHeader,
  ]);

  /** Add this event to the queue to be processed later */
  const enqueueEventEmitter = (eventEmitter: EventEmitter): void => {
    eventQueue.current.push(eventEmitter);
  };

  /** We never want failures in analytics emission to impact our components */
  const emitSafely = (eventEmitter: EventEmitter): void => {
    try {
      eventEmitter();
    } catch (e) {
      console.error('Failed to emit analytics event.', e);
    }
  };

  /**
   * Accepts a callback which invokes an event tracking call on
   * {@link analytics}. If this is invoked before we have finished initializing
   * analytics for the page, enqueue them on {@link eventQueue}. If we're
   * already initialized, we can fire the event immediately.
   *
   * We only use refs as dependencies for this callback to ensure functions
   * derived from it never trigger re-renders.
   */
  const emitEvent = useCallback(
    (eventEmitter: EventEmitter): void => {
      if (analyticsDisabled.current) return;

      if (analyticsInitializedRef.current) {
        processPreInitEventQueue();
        emitSafely(eventEmitter);
      } else {
        enqueueEventEmitter(eventEmitter);
      }
    },
    [analyticsInitializedRef, processPreInitEventQueue, analyticsDisabled],
  );

  return {
    /**
     * True when SPA Analytics have been successfully initialized. Since this is
     * expected to be used to block rendering for components which absolutely
     * require analytics to be initialized before they render (e.g. Nav SPA),
     * this will also go to true if SPA Analytics fails to initialize or wasn't
     * enabled in this environment at all.
     */
    analyticsInitialized,
    /**
     * Enqueues an analytics tracking callback for emission. If invoked before
     * analytics are fully initialized, the event will be queued until
     * initialization is complete. If invoked after initialization, the event
     * will be emitted immediately. If analytics failed to initialize or wasn't
     * enabled in this environment, this will no-op.
     */
    emitEvent,
  };
};
